import { STORAGE_KEYS } from './constants';
import { getTokenSyncParsed } from './token-slot';

/**
 * The refresh token, held in memory while the page lives.
 *
 * The IdP rotates refresh tokens and revokes the whole family if one is replayed, so the
 * rule everything here serves is: one handle, one holder. While the page is alive the
 * handle exists only in this module, which no second consumer can reach. sessionStorage
 * as the home of the token would break the rule — it is copied into a duplicated tab and
 * a `target=_blank` child, and both copies would then present the same handle.
 *
 * Every handle is bound to the identity of the access token it arrived with (see
 * `identity.ts`). Tabs share one access-token slot, and another user may have logged in
 * since; a handle is only ever used while the slot still holds its own identity.
 *
 * Kept only in memory, though, the token died with every reload, and the first renewal
 * after one had to go through the hidden `prompt=none` frame. That frame depends on the
 * IdP's cookie, which is third-party from the app's origin: a browser that blocks those
 * (Safari, Brave, incognito, the Chrome setting) fails the renewal and ends the session
 * an hour after every reload.
 *
 * So the token is handed across a reload through sessionStorage, but never stored there
 * while a page holds it:
 *   - `pagehide` moves it from memory to storage;
 *   - the next document in the tab takes it from storage into memory as it boots,
 *     removing the stored copy before anything else runs.
 * Storage holds the token only between two documents, when no live page holds it, so a
 * tab duplicated or opened from a live page copies nothing.
 *
 * The back/forward cache needs one more step. Its `pagehide` (`persisted`) promises the
 * document may come back, but the browser can still evict it and load a fresh document
 * on Back — Chrome does exactly that to this app. So the frozen document hands the token
 * over as any other would, and keeps a copy that is only valid if it is restored before
 * anyone takes the stored one: on `pageshow` it takes the token back only if storage
 * still holds that same token, and otherwise forgets its copy. One sequence still copies
 * the handle: duplicating the tab while it shows another site with this app in its cache,
 * then going Back in both. The IdP's reuse detection then ends the family, which costs
 * the same as a lost handle — the renewal falls back to the frame.
 *
 * When other tabs of the same identity are open, `pagehide` gives the token to one of
 * them instead (`setRefreshTokenHandoff`): it may be the tab closing, and then nothing
 * would be left to renew with. Either there or into storage — never both.
 *
 * Anything that goes wrong on the way — storage unavailable, the tab crashing before
 * `pagehide` — loses the token, and the renewal falls back to the `prompt=none` frame as
 * it always did. Losing a handle is safe; duplicating one is not.
 */

export interface HeldRefreshToken {
  token: string;
  /** Identity key of the access token the handle arrived with. */
  identityKey: string;
}

let current: HeldRefreshToken | undefined;

/**
 * Keeps a handle for the identity it was issued to. A handle whose identity is unknown
 * is not kept: it could not be checked against the slot before use.
 */
export function setRefreshToken(token: string | undefined, identityKey: string | undefined): void {
  if (token && !identityKey) {
    console.warn('[OIDC] Dropping a refresh token whose access token names no identity');
  }
  current = token && identityKey ? { token, identityKey } : undefined;
}

export function hasRefreshToken(): boolean {
  return current !== undefined;
}

/** The identity the held handle belongs to, if a handle is held. */
export function refreshTokenIdentity(): string | undefined {
  return current?.identityKey;
}

/**
 * Reads the token and drops it in the same step. A refresh token is single-use under
 * rotation, so handing the same handle out twice is the one thing that must not happen:
 * the second use looks like a replay and costs the whole family.
 */
export function takeRefreshToken(): HeldRefreshToken | undefined {
  const held = current;
  current = undefined;
  return held;
}

/** Drops the token, including a copy left for the next document. Used on signout. */
export function clearRefreshToken(): void {
  current = undefined;
  try {
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_HANDOFF);
  } catch (error) {
    console.warn('[OIDC] Failed to clear the refresh token handed over a reload:', error);
  }
}

interface HandoffRecord {
  rt: string;
  idKey: string;
}

function serialize(held: HeldRefreshToken): string {
  return JSON.stringify({ rt: held.token, idKey: held.identityKey } satisfies HandoffRecord);
}

function parse(raw: string): HeldRefreshToken | undefined {
  try {
    const record: unknown = JSON.parse(raw);
    if (
      typeof record === 'object' &&
      record !== null &&
      typeof (record as HandoffRecord).rt === 'string' &&
      typeof (record as HandoffRecord).idKey === 'string' &&
      (record as HandoffRecord).rt &&
      (record as HandoffRecord).idKey
    ) {
      return { token: (record as HandoffRecord).rt, identityKey: (record as HandoffRecord).idKey };
    }
  } catch {
    // Falls through: an unreadable record is no handle at all.
  }
  return undefined;
}

/**
 * Takes the token the previous document in this tab left behind, if any, provided it
 * belongs to whoever is in the access-token slot now (`currentIdentityKey`).
 *
 * Must run before anything can renew, and in the silent-renew frame not at all: the
 * frame shares the tab's sessionStorage, and a token it took would be stranded in its
 * realm. The stored copy is removed before the token is adopted, and if it cannot be
 * removed the token is not adopted — a copy left in storage while this page holds the
 * handle is exactly what a duplicated tab would pick up.
 */
export function restoreRefreshTokenAfterReload(currentIdentityKey: string | undefined): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN_HANDOFF);
    if (raw === null) return;
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_HANDOFF);
  } catch (error) {
    console.warn('[OIDC] Failed to take the refresh token handed over a reload:', error);
    return;
  }

  const handedOver = parse(raw);
  if (!handedOver) return;

  // Somebody else logged in on this origin while the tab reloaded.
  if (handedOver.identityKey !== currentIdentityKey) {
    console.warn('[OIDC] Dropping a refresh token handed over for another identity');
    return;
  }

  // Nothing sets a token before this runs; should something ever, the newer one stays.
  current ??= handedOver;
}

let handOffToAnotherTab: ((held: HeldRefreshToken) => boolean) | null = null;

/**
 * Lets `pagehide` give the token to another tab instead of this tab's next document (see
 * `refresh-coordination.ts`). The hook returns whether it sent the token; it must be
 * synchronous, as nothing runs after `pagehide` returns.
 */
export function setRefreshTokenHandoff(hook: ((held: HeldRefreshToken) => boolean) | null): void {
  handOffToAnotherTab = hook;
}

/**
 * Hands the token to the next document in this tab when this one is hidden, and takes it
 * back if this one returns from the back/forward cache. Returns the function that removes
 * the listeners.
 *
 * `pagehide` and not `visibilitychange`, which fires on every tab switch and would leave
 * the token in storage under a live page; not `beforeunload`, which can still be followed
 * by the page staying.
 */
export function handOverRefreshTokenAcrossReloads(target: Window = window): () => void {
  // What this document put in storage, kept only to recognise it on a bfcache restore.
  let handedOver: { held: HeldRefreshToken; raw: string } | undefined;

  const onPageHide = () => {
    if (!current) return;

    // No access token left means the session ended — signed out here or in another tab.
    // A handle from it is not handed to anyone: the next login must start a new family.
    if (!getTokenSyncParsed()) {
      current = undefined;
      return;
    }

    // Another tab first: it outlives this document whether it is closing or reloading.
    // Once sent there, the token must not be stored for this tab's next document too.
    let sentToAnotherTab = false;
    try {
      sentToAnotherTab = handOffToAnotherTab?.(current) ?? false;
    } catch (error) {
      console.warn('[OIDC] Failed to hand the refresh token to another tab:', error);
    }
    if (sentToAnotherTab) {
      current = undefined;
      return;
    }

    const raw = serialize(current);
    try {
      sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN_HANDOFF, raw);
    } catch (error) {
      console.warn('[OIDC] Failed to hand the refresh token over the reload:', error);
      return;
    }
    handedOver = { held: current, raw };
    // Dropped from memory once it is in storage: a renewal still running in this document
    // as it goes must not present the handle the next document is about to use.
    current = undefined;
  };

  const onPageShow = (event: PageTransitionEvent) => {
    if (!event.persisted || !handedOver) return;

    const ours = handedOver;
    handedOver = undefined;
    try {
      // Taken by a fresh document while this one sat in the cache, or replaced by a later
      // one: either way the handle is somebody else's now.
      if (sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN_HANDOFF) !== ours.raw) return;
      sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN_HANDOFF);
    } catch (error) {
      console.warn('[OIDC] Failed to take the refresh token back from storage:', error);
      return;
    }
    current ??= ours.held;
  };

  target.addEventListener('pagehide', onPageHide);
  target.addEventListener('pageshow', onPageShow);
  return () => {
    target.removeEventListener('pagehide', onPageHide);
    target.removeEventListener('pageshow', onPageShow);
  };
}
